import { useRef, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { BRAND } from "@avestate/shared";
import { useAuth } from "@/auth/useAuth.js";
import { Button } from "@/components/ui/button.js";
import LoginBackdrop from "./LoginBackdrop.js";

export default function Login(): JSX.Element {
  const { session, signInWithTotp, ready } = useAuth();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (ready && session) return <Navigate to="/" replace />;

  async function submit(value: string) {
    if (submitting) return;
    setSubmitting(true);
    const r = await signInWithTotp(value);
    setSubmitting(false);
    if (!r.ok) {
      toast.error(r.error ?? "Accesso non riuscito");
      setCode("");
      inputRef.current?.focus();
    }
    // on success the session updates and the <Navigate> above redirects
  }

  function onChange(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    if (digits.length === 6) void submit(digits);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (code.length === 6) void submit(code);
  }

  return (
    <main className="relative grid min-h-screen place-items-center bg-bg px-6">
      {/* Sfondo: la dashboard REALE renderizzata live (componenti React, non
          screenshot) — ma con dati demo hardcoded e cifre economiche oscurate:
          ZERO chiamate API prima dell'autenticazione. inert + pointer-events
          -none + select-none = nessuna interazione possibile fino al TOTP. */}
      <div
        aria-hidden
        // @ts-expect-error — inert è supportato dai browser ma non ancora nei tipi React
        inert=""
        className="pointer-events-none absolute inset-0 select-none overflow-hidden"
      >
        <LoginBackdrop />
      </div>
      {/* Liquid Glass (stile iOS 26): la card È il vetro — blur/saturazione
          reali del contenuto dietro via backdrop-filter, bordo speculare
          luminoso in alto, tinta minima. Lo sfondo resta ben visibile. */}
      <div
        className="relative w-full max-w-[420px] space-y-8 px-8 py-10"
        style={{
          borderRadius: "28px",
          background: "linear-gradient(160deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.045) 45%, rgba(255,255,255,0.08) 100%)",
          backdropFilter: "blur(26px) saturate(170%)",
          WebkitBackdropFilter: "blur(26px) saturate(170%)",
          border: "1px solid rgba(255,255,255,0.16)",
          boxShadow: [
            "inset 0 1px 0 rgba(255,255,255,0.28)",      // riflesso speculare bordo alto
            "inset 0 -1px 0 rgba(255,255,255,0.06)",     // micro-riflesso bordo basso
            "inset 1px 0 0 rgba(255,255,255,0.08)",      // rim laterale
            "0 28px 70px rgba(0,0,0,0.55)",              // profondità
          ].join(", "),
        }}
      >
        <header className="text-center">
          <p className="font-display text-display tracking-tight text-white">
            {BRAND.wordmark.left}<span className="text-accent">{BRAND.wordmark.slash}</span>{BRAND.wordmark.right}
          </p>
          <p className="mt-2 font-mono text-meta uppercase tracking-wider text-white">
            admin · accesso staff
          </p>
        </header>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="space-y-2 text-center">
            <div className="mx-auto flex size-11 items-center justify-center rounded-pill bg-accent/10 text-accent">
              <ShieldCheck className="size-5" />
            </div>
            <p className="text-body text-white">
              Inserisci il codice a 6 cifre dalla tua app di autenticazione.
            </p>
          </div>

          <input
            ref={inputRef}
            inputMode="numeric"
            autoComplete="one-time-code"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            aria-label="Codice di autenticazione a 6 cifre"
            value={code}
            onChange={(e) => onChange(e.target.value)}
            disabled={submitting}
            placeholder="000000"
            className="w-full rounded-xl py-4 text-center font-mono text-[34px] tracking-[0.5em] text-white tabular-nums outline-none transition-colors duration-220 placeholder:text-white/50 focus:border-accent disabled:opacity-60"
            style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)" }}
          />

          <Button
            type="submit"
            disabled={submitting || code.length !== 6}
            className="w-full font-semibold disabled:opacity-80"
            style={{
              // Blu acceso e saturo, ben leggibile sul vetro scuro
              background: "#0a84ff",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.22)",
              boxShadow: "none",
            }}
          >
            {submitting ? "Verifico…" : "Accedi"}
          </Button>
        </form>

        <a
          href="https://avestate.avstudio.ai"
          className="flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-body text-white transition-colors duration-220 hover:text-white"
          style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)" }}
        >
          ← Torna al sito
        </a>
      </div>
    </main>
  );
}
